type MessageHandler = (payload: any) => void;
type VoidHandler = () => void;

// Resolve credenciais FRESCAS a cada (re)conexão: o token pode ter sido
// renovado pelo refresh do axios desde a 1ª conexão. Reusar o token velho fazia
// o backend fechar o handshake e o reconnect ficava preso (banner "reconectando"
// sem reconectar). Retorna null se ainda não há credenciais.
type CredentialsResolver = () => Promise<{ userId: string; token: string } | null>;

const MAX_BACKOFF_MS = 30000;
// Após reconexões repetidas, o delay cresce até o cap e CONTINUA tentando — o
// reconnect nunca desiste de vez (antes parava após N e travava para sempre).
const MAX_FAST_ATTEMPTS = 6;

class WebSocketService {
  private ws: WebSocket | null = null;

  private handlers: Map<string, MessageHandler[]> = new Map();

  private reconnectAttempts = 0;

  private resolveCredentials: CredentialsResolver | null = null;

  private intentionalDisconnect = false;

  private openHandlers: VoidHandler[] = [];

  private closeHandlers: VoidHandler[] = [];

  private pingInterval: ReturnType<typeof setInterval> | null = null;

  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Inicia a conexão. `resolveCredentials` é chamado a CADA (re)conexão para
   * obter userId + token atualizados (não fixa o token da 1ª conexão).
   */
  start(resolveCredentials: CredentialsResolver) {
    this.resolveCredentials = resolveCredentials;
    this.intentionalDisconnect = false;
    this.reconnectAttempts = 0;
    this.connect().catch(() => {
      /* connect() trata seus próprios erros e reagenda; nada a fazer aqui */
    });
  }

  private async connect() {
    if (this.intentionalDisconnect || !this.resolveCredentials) return;

    const creds = await this.resolveCredentials();
    if (!creds || !creds.userId) {
      // Sem credenciais agora — tenta de novo mais tarde (não desiste).
      this.scheduleReconnect();
      return;
    }

    // Limpa qualquer socket anterior antes de abrir um novo: sem isso, o socket
    // velho mantinha handlers ativos e seus closes "cruzados" embaralhavam o
    // contador de tentativas, levando o reconnect a morrer cedo.
    this.cleanupSocket();

    const wsUrl = process.env.REACT_APP_CONTACHAT_WS_URL || 'ws://localhost:8001';
    const ws = new WebSocket(`${wsUrl}/ws/${creds.userId}?token=${creds.token}`);
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.startPing();
      this.openHandlers.forEach(h => h());
    };

    ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        const list = this.handlers.get(data.type) || [];
        list.forEach(h => h(data.data));
      } catch {
        // ignore malformed messages
      }
    };

    ws.onerror = () => {
      // onclose será chamado em seguida — o tratamento de reconexão fica lá.
    };

    ws.onclose = event => {
      this.stopPing();
      this.closeHandlers.forEach(h => h());
      if (this.intentionalDisconnect) return;
      // 4001 = token inválido: reconectar com o mesmo token não adianta. Mesmo
      // assim reagendamos UMA vez com backoff, pois o resolver pode trazer um
      // token renovado (refresh do axios) na próxima tentativa.
      this.scheduleReconnect(event.code === 4001);
    };
  }

  // Agenda a próxima tentativa com backoff exponencial limitado. NUNCA para de
  // tentar (até disconnect()): após o teto de tentativas rápidas, mantém o
  // intervalo no cap (30s), em vez de desistir para sempre.
  private scheduleReconnect(slow = false) {
    if (this.intentionalDisconnect) return;
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    const expo = Math.min(this.reconnectAttempts, MAX_FAST_ATTEMPTS);
    let delay = Math.min(1000 * 2 ** expo, MAX_BACKOFF_MS);
    if (slow) delay = MAX_BACKOFF_MS;
    this.reconnectAttempts += 1;
    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch(() => {
        /* connect() trata seus próprios erros e reagenda; nada a fazer aqui */
      });
    }, delay);
  }

  private cleanupSocket() {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      try {
        this.ws.close();
      } catch {
        // ignore
      }
      this.ws = null;
    }
  }

  disconnect() {
    this.intentionalDisconnect = true;
    this.stopPing();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.cleanupSocket();
  }

  private startPing() {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      // Heartbeat abreviado (keepalive): backend responde {"t":"ack"}
      this.send({ t: 'hb' });
    }, 30000);
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  onOpen(handler: VoidHandler) {
    this.openHandlers.push(handler);
  }

  offOpen(handler: VoidHandler) {
    this.openHandlers = this.openHandlers.filter(h => h !== handler);
  }

  onClose(handler: VoidHandler) {
    this.closeHandlers.push(handler);
  }

  offClose(handler: VoidHandler) {
    this.closeHandlers = this.closeHandlers.filter(h => h !== handler);
  }

  on(type: string, handler: MessageHandler) {
    if (!this.handlers.has(type)) this.handlers.set(type, []);
    this.handlers.get(type)!.push(handler);
  }

  off(type: string, handler: MessageHandler) {
    const list = this.handlers.get(type);
    if (list) {
      this.handlers.set(
        type,
        list.filter(h => h !== handler),
      );
    }
  }

  subscribe(conversationId: string) {
    this.send({ type: 'subscribe', conversation_id: conversationId });
  }

  sendTyping(conversationId: string) {
    this.send({ type: 'typing', conversation_id: conversationId });
  }

  private send(data: any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }
}

export const wsService = new WebSocketService();
export default wsService;
